import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiExternalLink,
  FiShoppingBag,
  FiClock,
  FiCreditCard,
  FiHeart,
  FiLayout,
  FiShield,
  FiArrowRight,
} from 'react-icons/fi';
import SEO from '../../components/SEO';
import './CaseStudy.css';

/**
 * BabyBunnyResaleCaseStudy — /portfolio/babybunnyresale. Same CaseStudy
 * .css scaffolding as the others; accent is the site's brand orange.
 * Copy leads with the hybrid auction + buy-it-now model and the
 * warmer, parent-first brand voice that sets it apart from the usual
 * kids' resale channels.
 *
 * Status: in active development. No launched-metrics claims in copy —
 * final Results section is framed as "In Progress" instead.
 */
const ACCENT = '#ED6A2D';
const LIVE_URL = 'https://babybunnyresale.com/';
const IMG_MAC = '/images/portfolio/babybunnyresale-macbook.webp';
// Share tags and structured data need an absolute URL.
const IMG_MAC_ABSOLUTE = `https://silverfoxmedia.co${IMG_MAC}`;

const BabyBunnyResaleCaseStudy = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Baby Bunny Resale — Case Study',
    headline: 'Peer-to-Peer Kids Marketplace with Hybrid Auctions + Buy-It-Now',
    description:
      "A peer-to-peer resale marketplace built specifically for parents — hybrid model that supports both timed auctions and buy-it-now listings, wrapped in a warm, kid-friendly brand voice that sets it apart from the usual online resale channels. Currently in active development.",
    image: IMG_MAC_ABSOLUTE,
    url: 'https://silverfoxmedia.co/portfolio/babybunnyresale',
    author: {
      '@type': 'Organization',
      name: 'SilverFox Media',
      url: 'https://silverfoxmedia.co',
    },
    about: [
      { '@type': 'Thing', name: 'E-Commerce' },
      { '@type': 'Thing', name: 'Auction Platform' },
      { '@type': 'Thing', name: 'Peer-to-Peer Marketplace' },
      { '@type': 'Thing', name: 'Kids Resale' },
    ],
  };

  return (
    <>
      <SEO
        title="Baby Bunny Resale Case Study: Kids Marketplace with Hybrid Auctions"
        description="How SilverFox Media designed and built Baby Bunny Resale: a peer-to-peer resale marketplace for parents, with hybrid timed auctions and buy-it-now listings, custom bidding logic, Stripe payments, and a warm kid-friendly brand voice. In active development."
        keywords="Baby Bunny Resale case study, kids marketplace, peer-to-peer resale, hybrid auction buy-it-now, MERN e-commerce, Stripe marketplace, React auction platform, MongoDB e-commerce, custom auction system"
        image="https://silverfoxmedia.co/images/og/babybunnyresale.jpg"
        url="/portfolio/babybunnyresale"
        type="article"
        schema={structuredData}
      />

      <article
        className="case-study"
        style={{ '--case-study-accent': ACCENT }}
      >
        {/* HERO */}
        <section className="case-study__hero">
          <div className="container">
            <div className="case-study__hero-grid">
              <div className="case-study__hero-inner">
                <Link to="/portfolio" className="case-study__back">
                  <FiArrowLeft />
                  Back to Portfolio
                </Link>

                <span className="case-study__category">E-Commerce</span>

                <h1 className="case-study__title">
                  Baby Bunny Resale:{' '}
                  <span className="case-study__title-accent">
                    A Warmer, Friendlier Kids Marketplace
                  </span>
                </h1>

                <p className="case-study__tagline">
                  Peer-to-peer resale built specifically for parents — hybrid
                  timed auctions and buy-it-now listings, custom bidding logic,
                  Stripe payments, and a distinctive kid-friendly brand voice
                  that stands apart from the usual online resale channels.
                  Currently in active development.
                </p>

                <div className="case-study__hero-cta">
                  <a
                    href={LIVE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="case-study__cta-primary"
                  >
                    Visit Live Site <FiExternalLink />
                  </a>
                  <Link to="/contact" className="case-study__cta-secondary">
                    Start Your Project <FiArrowRight />
                  </Link>
                </div>

                <div className="case-study__hero-meta">
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Client</span>
                    <span className="case-study__hero-meta-value">SilverFox Media</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Industry</span>
                    <span className="case-study__hero-meta-value">E-Commerce / Kids Resale</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Timeline</span>
                    <span className="case-study__hero-meta-value">Ongoing — 1yr in build</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Role</span>
                    <span className="case-study__hero-meta-value">Solo Design + Full-Stack</span>
                  </div>
                </div>
              </div>

              <div className="case-study__hero-media">
                <img
                  src={IMG_MAC}
                  alt="Baby Bunny Resale homepage displayed on a MacBook mockup"
                  className="case-study__hero-macbook"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED IMAGE */}
        <section className="case-study__featured">
          <div className="container">
            <div className="case-study__featured-image-wrap">
              <img
                src={IMG_MAC}
                alt="Baby Bunny Resale homepage displayed on a MacBook mockup"
                className="case-study__featured-image"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Overview</p>
            <h2 className="case-study__section-title">
              A resale marketplace that feels made for parents, not scraped off a template.
            </h2>
            <div className="case-study__prose">
              <p>
                The kids' resale category is dominated by hostile, transactional
                marketplaces. Craigslist, Facebook Marketplace, local buy/sell
                groups where every listing looks identical and every interaction
                is a negotiation with a stranger. Baby Bunny Resale takes a
                different approach: a peer-to-peer marketplace built
                specifically for parents, with a warm, welcoming brand voice
                and a layout that makes browsing feel less like scavenging and
                more like shopping.
              </p>
              <p>
                Under the hood it's a hybrid marketplace. Some items belong on
                a timed auction (rare finds, size-specific pieces, higher-value
                gear). Others belong on a straightforward buy-it-now (staples
                like onesies, monitors, teethers). The platform supports both
                without making the UX feel bloated, and Stripe handles the
                payments end-to-end so the seller sees money in their account
                and the buyer gets a clean checkout.
              </p>
            </div>
          </div>
        </section>

        {/* CHALLENGE */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Challenge</p>
            <h2 className="case-study__section-title">
              Parents already have too many places to sell used baby gear. Why another one?
            </h2>
            <div className="case-study__prose">
              <p>
                Whatever we built had to give parents a reason to trust yet
                another marketplace — not "the biggest inventory," not "the
                cheapest fees," but a place that treats them like the target
                audience instead of an afterthought. That meant the brand voice,
                the layouts, the product photography rules, and even the
                default filters had to be designed with a parent's day in mind.
              </p>
              <p>
                It also meant supporting two very different transaction models
                on the same platform. Auctions and buy-it-now behave nothing
                alike — one is time-bound, competitive, and needs live bid
                tracking; the other is instant and needs frictionless checkout.
                Both had to feel like first-class citizens of the same site.
              </p>
              <p>
                And the whole thing had to be maintainable by one person. Solo
                design, solo full-stack, solo everything. That constraint drove
                a lot of the architectural decisions: MERN over anything
                heavier, Stripe over a custom payment stack, MongoDB over a
                relational store that would need a DBA.
              </p>
            </div>
          </div>
        </section>

        {/* SOLUTION / KEY FEATURES */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Solution</p>
            <h2 className="case-study__section-title">
              Hybrid marketplace, kid-friendly brand, MERN stack under the hood.
            </h2>
            <p className="case-study__section-lead">
              React frontend, Node + Express API, MongoDB store, Stripe for
              payments. Custom bidding logic layered on top so auctions and
              buy-it-now listings coexist without one feeling bolted on.
            </p>

            <div className="case-study__features">
              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiClock /></span>
                <h3 className="case-study__feature-title">Timed Auctions</h3>
                <p className="case-study__feature-body">
                  Sellers can list items as timed auctions with a start price,
                  bid history, and a countdown to close. Custom bidding logic
                  handles the last-minute activity that always concentrates
                  around the end window.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiShoppingBag /></span>
                <h3 className="case-study__feature-title">Buy-It-Now Listings</h3>
                <p className="case-study__feature-body">
                  For the staples — onesies, monitors, teethers — a
                  straightforward buy-it-now flow. Fixed price, add to cart,
                  Stripe checkout. Same seller dashboard, same messaging tools,
                  same experience.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCreditCard /></span>
                <h3 className="case-study__feature-title">Stripe Payments</h3>
                <p className="case-study__feature-body">
                  Stripe handles the money end-to-end. Buyers get a clean,
                  familiar checkout; sellers get money into their Stripe
                  account without the platform holding funds. No custom payout
                  logic to maintain.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiHeart /></span>
                <h3 className="case-study__feature-title">Parent-First Brand</h3>
                <p className="case-study__feature-body">
                  Three-bunny mascot, warm orange and teal palette, friendly
                  copy on every screen. Every design decision leans toward
                  making a parent feel welcomed rather than transacted at.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiLayout /></span>
                <h3 className="case-study__feature-title">Responsive from Day One</h3>
                <p className="case-study__feature-body">
                  Most parents are on their phones. The layouts, photo grids,
                  and bid interactions all work mobile-first — desktop is just
                  the wider version of the same UX, not a separate design.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiShield /></span>
                <h3 className="case-study__feature-title">Solo-Maintainable Stack</h3>
                <p className="case-study__feature-body">
                  MERN, Stripe, one hosting provider. Deliberately boring
                  architecture so one person can run it. No microservices, no
                  event-driven glue, no custom infrastructure — just software
                  that ships.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TECH STACK */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Stack</p>
            <h2 className="case-study__section-title">React + Node + MongoDB + Stripe.</h2>
            <p className="case-study__section-lead">
              Purpose-picked stack — nothing exotic, nothing that needs a team
              to keep running.
            </p>

            <div className="case-study__tech">
              <span className="case-study__tech-chip">React</span>
              <span className="case-study__tech-chip">Node.js</span>
              <span className="case-study__tech-chip">Express</span>
              <span className="case-study__tech-chip">MongoDB</span>
              <span className="case-study__tech-chip">Mongoose</span>
              <span className="case-study__tech-chip">Stripe</span>
              <span className="case-study__tech-chip">Custom Auction Logic</span>
              <span className="case-study__tech-chip">AWS S3</span>
              <span className="case-study__tech-chip">Render</span>
            </div>
          </div>
        </section>

        {/* IN PROGRESS */}
        <section className="case-study__section case-study__section--dark">
          <div className="container">
            <p className="case-study__section-label">In Progress</p>
            <h2 className="case-study__section-title">
              One year in, still building. Aiming for a public launch soon.
            </h2>
            <p className="case-study__section-lead" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Baby Bunny Resale is a work in progress. Roughly a year of solo
              design and development so far, with the brand system, the core
              marketplace flows, and the hybrid auction + buy-it-now
              architecture all in place. The remaining work is polish, trust
              patterns, and getting the first real listings live.
            </p>

            <div className="case-study__stats">
              <div className="case-study__stat">
                <p className="case-study__stat-value">1yr</p>
                <p className="case-study__stat-label">Of solo design + development to date</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">2</p>
                <p className="case-study__stat-label">Transaction models (auction + buy-it-now) in one platform</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">1</p>
                <p className="case-study__stat-label">Person designing, building, and shipping every piece</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="case-study__section">
          <div className="container">
            <div className="case-study__cta">
              <h2 className="case-study__cta-title">Have a marketplace idea in mind?</h2>
              <p className="case-study__cta-text">
                Whether it's a resale marketplace, a bidding platform, a
                two-sided marketplace with escrow and disputes — we design and
                build e-commerce end-to-end. Let's talk.
              </p>
              <Link to="/contact" className="case-study__cta-btn">
                Start Your Project <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default BabyBunnyResaleCaseStudy;
